import { unified, type PluggableList, type Processor } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import rehypeKatex from 'rehype-katex';
import { VFile } from 'vfile';
import type { Root as HastRoot } from 'hast';
import type { Root as MdastRoot } from 'mdast';
import remarkAlerts from './plugins/remarkAlerts';
import remarkDefinitionLists from './plugins/remarkDefinitionLists';
import remarkCodeMeta from './plugins/remarkCodeMeta';
import rehypeFixFragmentLinks from './plugins/rehypeFixFragmentLinks';
import rehypeCollectHeadings from './plugins/rehypeCollectHeadings';
import rehypeFeatureGates from './plugins/rehypeFeatureGates';
import { sanitizeSchema } from './security/sanitizeSchema';
import { resolveViewerOptions, type ViewerOptions } from '../config/viewerOptions';
import type { Heading } from '../types';

export interface ParsedMarkdown {
  tree: HastRoot;
  headings: Heading[];
}

/** Markdown in, sanitized HAST out. */
export type MarkdownProcessor = Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined>;

/**
 * Assembles the plugin list.
 *
 * Order is security-critical:
 *
 *   remark-rehype (raw passthrough) -> rehype-raw -> rehype-sanitize -> everything else
 *
 * `rehype-raw` turns embedded HTML strings into real nodes and `rehype-sanitize`
 * immediately prunes them. Anything that generates trusted markup (slugs, KaTeX)
 * runs *after* the sanitizer so its output is not stripped -- and because that
 * output derives from already-sanitized content, it cannot reintroduce script.
 */
const buildPlugins = (options: ViewerOptions): PluggableList => {
  const plugins: PluggableList = [remarkParse, [remarkGfm, { singleTilde: false }], remarkCodeMeta];

  if (options.enableCallouts) plugins.push(remarkAlerts);
  if (options.enableDefinitionLists) plugins.push(remarkDefinitionLists);
  if (options.enableMath) plugins.push([remarkMath, { singleDollarTextMath: true }]);

  plugins.push([
    remarkRehype,
    {
      allowDangerousHtml: options.enableHtml,
      // Left empty so the sanitizer owns id prefixing exactly once.
      clobberPrefix: '',
      footnoteLabel: 'Footnotes',
      footnoteBackLabel: 'Back to reference',
    },
  ]);

  if (options.enableHtml) plugins.push(rehypeRaw);
  if (options.sanitizeHtml) plugins.push([rehypeSanitize, sanitizeSchema]);

  plugins.push(
    [rehypeFeatureGates, { enableFootnotes: options.enableFootnotes }],
    rehypeSlug,
    [rehypeFixFragmentLinks, { clobberPrefix: options.sanitizeHtml ? 'user-content-' : '' }],
  );

  if (options.enableMath) {
    plugins.push([
      rehypeKatex,
      { throwOnError: false, trust: false, strict: false, output: 'htmlAndMathml' },
    ]);
  }

  plugins.push(rehypeCollectHeadings);

  return plugins;
};

export const createMarkdownProcessor = (overrides?: Partial<ViewerOptions>): MarkdownProcessor =>
  unified().use(buildPlugins(resolveViewerOptions(overrides))) as unknown as MarkdownProcessor;

const cache = new Map<string, MarkdownProcessor>();

const getProcessor = (options: ViewerOptions): MarkdownProcessor => {
  const key = JSON.stringify(options);
  let processor = cache.get(key);
  if (!processor) {
    processor = createMarkdownProcessor(options);
    cache.set(key, processor);
  }
  return processor;
};

/**
 * Parses Markdown into a sanitized HAST tree plus its heading outline.
 *
 * Never throws: a parser failure degrades to the raw source in a `pre` so the
 * viewer can still show something useful.
 */
export const parseMarkdown = (
  content: string,
  overrides?: Partial<ViewerOptions>,
): ParsedMarkdown => {
  const options = resolveViewerOptions(overrides);

  try {
    const processor = getProcessor(options);
    const file = new VFile({ value: content });
    const tree = processor.runSync(processor.parse(file), file);
    return { tree, headings: file.data.headings ?? [] };
  } catch (error) {
    console.error('Markdown parsing failed; falling back to plain text.', error);
    return {
      tree: {
        type: 'root',
        children: [
          {
            type: 'element',
            tagName: 'pre',
            properties: { className: ['markdown-parse-fallback'] },
            children: [{ type: 'text', value: content }],
          },
        ],
      },
      headings: [],
    };
  }
};

/** @deprecated Use {@link createMarkdownProcessor}. */
export const createMarkdownPipeline = createMarkdownProcessor;
